import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  User,
  signOut,
  setPersistence,
  browserLocalPersistence,
  inMemoryPersistence,
} from "firebase/auth";
import firebaseConfig from "../../firebase-applet-config.json";

const EXPECTED_FIREBASE_PROJECT_ID = "gen-lang-client-0902287543";

const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
export const auth = getAuth(app);

type AuthResult = { user: User; accessToken: string | null };

export type DriveTokenReconnectResult = {
  user: User;
  accessToken: string;
  refreshed: boolean;
};

let isSigningIn = false;
let isReconnectingDriveToken = false;
let reconnectInFlightPromise: Promise<DriveTokenReconnectResult> | null = null;
let cachedAccessToken: string | null = null;
let cachedAccessTokenExpiresAt = 0;
let lastAuthUser: User | null = null;
let customerBenchModeActive = false;

const CUSTOMER_BENCH_MODE_KEY = "customer_bench_device_mode";
const DRIVE_TOKEN_KEY = "gdrive_access_token";
const DRIVE_TOKEN_EXPIRES_KEY = "gdrive_access_token_expires_at";
const AUTH_MODE_KEY = "rodgerspc_auth_mode";

// Google OAuth access tokens are short-lived.
// Keep a conservative lifetime so Drive evidence loading does not use stale tokens.
const DRIVE_TOKEN_SAFE_LIFETIME_MS = 50 * 60 * 1000;

const GOOGLE_PROMPT_CUSTOMER_BENCH = "select_account consent";
// Normal shop device should not force account chooser/consent on every Drive token refresh.
// If the browser already has a valid Google session, this lets the OAuth popup resolve with minimal friction.
const GOOGLE_PROMPT_SHOP_DEVICE = "";

const APP_STORAGE_KEY_PREFIXES = [
  "gdrive_",
  "rodgerspc_",
  "rpc_",
  "command_center_",
];

const FIREBASE_STORAGE_KEY_PREFIXES = ["firebase:", "firebaseLocalStorage"];

const APP_STORAGE_EXACT_KEYS = [
  CUSTOMER_BENCH_MODE_KEY,
  DRIVE_TOKEN_KEY,
  DRIVE_TOKEN_EXPIRES_KEY,
  AUTH_MODE_KEY,
];

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function isTokenStillFresh(expiresAt: number): boolean {
  return Number.isFinite(expiresAt) && expiresAt > Date.now() + 60_000;
}

function isAppOwnedStorageKey(key: string, includeFirebase = false): boolean {
  return (
    APP_STORAGE_EXACT_KEYS.includes(key) ||
    APP_STORAGE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix)) ||
    (includeFirebase &&
      FIREBASE_STORAGE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix)))
  );
}

function removeStorageKey(storage: Storage, key: string) {
  try {
    storage.removeItem(key);
  } catch {
    // ignore storage failure
  }
}

function clearStorageBucket(
  storage: Storage,
  options?: { preserveBenchFlag?: boolean; includeFirebase?: boolean },
) {
  const preserveBenchFlag = options?.preserveBenchFlag === true;
  const includeFirebase = options?.includeFirebase === true;

  for (let i = storage.length - 1; i >= 0; i--) {
    const key = storage.key(i);
    if (!key) continue;

    if (key === CUSTOMER_BENCH_MODE_KEY && preserveBenchFlag) continue;

    if (isAppOwnedStorageKey(key, includeFirebase)) {
      removeStorageKey(storage, key);
    }
  }
}

function clearAppOwnedWebStorage(options?: {
  preserveBenchFlag?: boolean;
  includeFirebase?: boolean;
}) {
  if (!isBrowser()) return;

  try {
    clearStorageBucket(localStorage, {
      preserveBenchFlag: false,
      includeFirebase: options?.includeFirebase === true,
    });
  } catch (e) {
    console.warn("Unable to clear app-owned localStorage:", e);
  }

  try {
    clearStorageBucket(sessionStorage, {
      preserveBenchFlag: options?.preserveBenchFlag === true,
      includeFirebase: options?.includeFirebase === true,
    });
  } catch (e) {
    console.warn("Unable to clear app-owned sessionStorage:", e);
  }
}

async function clearAppOwnedIndexedDbAndCaches() {
  if (!isBrowser()) return;

  try {
    if ("indexedDB" in window && typeof indexedDB.databases === "function") {
      const databases = await indexedDB.databases();

      await Promise.all(
        databases
          .map((db) => db.name)
          .filter((name): name is string => Boolean(name))
          .filter((name) => {
            const lowerName = name.toLowerCase();
            return (
              lowerName.includes("firebase") ||
              lowerName.includes("rodgerspc") ||
              lowerName.includes("command") ||
              lowerName.includes("gdrive")
            );
          })
          .map(
            (name) =>
              new Promise<void>((resolve) => {
                const req = indexedDB.deleteDatabase(name);
                req.onsuccess = () => resolve();
                req.onerror = () => resolve();
                req.onblocked = () => resolve();
              }),
          ),
      );
    }
  } catch (e) {
    console.warn("Unable to clear app-owned IndexedDB databases:", e);
  }

  try {
    if ("caches" in window) {
      const names = await caches.keys();

      await Promise.all(
        names
          .filter((name) => {
            const lowerName = name.toLowerCase();
            return (
              lowerName.includes("firebase") ||
              lowerName.includes("rodgerspc") ||
              lowerName.includes("command") ||
              lowerName.includes("gdrive")
            );
          })
          .map((name) => caches.delete(name)),
      );
    }
  } catch (e) {
    console.warn("Unable to clear app-owned cache storage:", e);
  }
}

function setCustomerBenchModeSessionFlag(isEnabled: boolean) {
  customerBenchModeActive = isEnabled;
  if (!isBrowser()) return;

  try {
    localStorage.removeItem(CUSTOMER_BENCH_MODE_KEY);
    localStorage.setItem(
      AUTH_MODE_KEY,
      isEnabled ? "customer_bench" : "shop_device",
    );
  } catch {
    // ignore
  }

  try {
    if (isEnabled) {
      sessionStorage.setItem(CUSTOMER_BENCH_MODE_KEY, "true");
    } else {
      sessionStorage.removeItem(CUSTOMER_BENCH_MODE_KEY);
    }
  } catch (e) {
    console.warn("Unable to store Customer Bench Device Mode session flag:", e);
  }
}

function readCustomerBenchModeStorageFlag(): boolean {
  if (!isBrowser()) return customerBenchModeActive;

  try {
    // Old builds stored this in localStorage. Remove it so customer-device mode does not persist across browser sessions.
    if (localStorage.getItem(CUSTOMER_BENCH_MODE_KEY) !== null) {
      localStorage.removeItem(CUSTOMER_BENCH_MODE_KEY);
    }
  } catch {
    // ignore
  }

  try {
    return sessionStorage.getItem(CUSTOMER_BENCH_MODE_KEY) === "true";
  } catch {
    return customerBenchModeActive;
  }
}

function safeClearStoredToken() {
  cachedAccessToken = null;
  cachedAccessTokenExpiresAt = 0;

  if (!isBrowser()) return;

  try {
    localStorage.removeItem(DRIVE_TOKEN_KEY);
    localStorage.removeItem(DRIVE_TOKEN_EXPIRES_KEY);
    sessionStorage.removeItem(DRIVE_TOKEN_KEY);
    sessionStorage.removeItem(DRIVE_TOKEN_EXPIRES_KEY);
  } catch (e) {
    console.warn("Unable to clear access token from storage:", e);
  }
}

function safeGetStoredToken(): string | null {
  if (!isBrowser() || customerBenchModeActive) {
    return null;
  }

  try {
    const storedToken =
      localStorage.getItem(DRIVE_TOKEN_KEY) ||
      sessionStorage.getItem(DRIVE_TOKEN_KEY);
    const storedExpiryRaw =
      localStorage.getItem(DRIVE_TOKEN_EXPIRES_KEY) ||
      sessionStorage.getItem(DRIVE_TOKEN_EXPIRES_KEY);
    const storedExpiry = storedExpiryRaw ? Number(storedExpiryRaw) : 0;

    if (!storedToken || !isTokenStillFresh(storedExpiry)) {
      safeClearStoredToken();
      return null;
    }

    cachedAccessToken = storedToken;
    cachedAccessTokenExpiresAt = storedExpiry;
    return storedToken;
  } catch (e) {
    console.warn(
      "Could not load cached Google Drive access token from storage:",
      e,
    );
    return null;
  }
}

function safeStoreToken(token: string, isCustomerBenchMode: boolean) {
  cachedAccessToken = token;
  cachedAccessTokenExpiresAt = Date.now() + DRIVE_TOKEN_SAFE_LIFETIME_MS;

  if (!isBrowser()) return;

  if (isCustomerBenchMode) {
    // Customer device mode is memory-only. The Drive token exists only in this JS runtime.
    try {
      localStorage.removeItem(DRIVE_TOKEN_KEY);
      localStorage.removeItem(DRIVE_TOKEN_EXPIRES_KEY);
      sessionStorage.removeItem(DRIVE_TOKEN_KEY);
      sessionStorage.removeItem(DRIVE_TOKEN_EXPIRES_KEY);
    } catch {
      // ignore
    }
    return;
  }

  try {
    // Normal RodgersPC shop device may reuse the token briefly, but never past the safe lifetime.
    localStorage.setItem(DRIVE_TOKEN_KEY, token);
    localStorage.setItem(
      DRIVE_TOKEN_EXPIRES_KEY,
      String(cachedAccessTokenExpiresAt),
    );
    sessionStorage.removeItem(DRIVE_TOKEN_KEY);
    sessionStorage.removeItem(DRIVE_TOKEN_EXPIRES_KEY);
  } catch (e) {
    console.warn("Unable to save access token to storage:", e);
  }
}

async function applyAuthPersistence(isCustomerBenchMode: boolean) {
  await setPersistence(
    auth,
    isCustomerBenchMode ? inMemoryPersistence : browserLocalPersistence,
  );
}

async function prepareCustomerBenchSignIn() {
  customerBenchModeActive = true;
  setCustomerBenchModeSessionFlag(true);
  safeClearStoredToken();

  // Customer / unknown device mode must not inherit a saved Firebase session.
  // Sign out first, switch to memory-only persistence, then clear app-owned storage.
  // Do not delete IndexedDB while signInWithPopup is actively running; do it before popup open.
  try {
    await signOut(auth);
  } catch {
    // already signed out or auth not ready
  }

  await applyAuthPersistence(true);

  clearAppOwnedWebStorage({ preserveBenchFlag: true, includeFirebase: true });
  await clearAppOwnedIndexedDbAndCaches();

  cachedAccessToken = null;
  cachedAccessTokenExpiresAt = 0;
  lastAuthUser = null;

  // Keep the bench flag after cleanup so the UI stays in safe mode.
  setCustomerBenchModeSessionFlag(true);
}

async function prepareNormalShopSignIn() {
  customerBenchModeActive = false;
  setCustomerBenchModeSessionFlag(false);
  await applyAuthPersistence(false);
  cachedAccessToken = safeGetStoredToken();
}

function buildGoogleProvider(
  isCustomerBenchMode: boolean,
  options?: { forcePrompt?: boolean; loginHint?: string | null },
): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();

  // Drive tree read + app-created file export/upload support.
  provider.addScope("https://www.googleapis.com/auth/drive.readonly");
  provider.addScope("https://www.googleapis.com/auth/drive.file");

  const params: Record<string, string> = {
    access_type: "online",
    include_granted_scopes: "true",
  };

  const shouldForcePrompt = options?.forcePrompt === true;

  if (isCustomerBenchMode) {
    // Customer / unknown device:
    // - no login_hint
    // - force chooser + consent
    // - max_age=0 asks Google to re-auth the account selection instead of silently reusing a prior session
    // This cannot erase a Google account already saved in Chrome itself, but it prevents Command Center
    // from steering the browser back into Joshua's account automatically.
    params.prompt = GOOGLE_PROMPT_CUSTOMER_BENCH;
    params.max_age = "0";
  } else if (shouldForcePrompt && GOOGLE_PROMPT_SHOP_DEVICE) {
    params.prompt = GOOGLE_PROMPT_SHOP_DEVICE;
  }

  const hint = options?.loginHint || getCurrentAuthUser()?.email || lastAuthUser?.email || "";
  if (!isCustomerBenchMode && hint) {
    params.login_hint = hint;
  }

  provider.setCustomParameters(params);

  return provider;
}

function getFriendlyAuthError(error: any): string {
  const code = String(error?.code || "");
  const message = String(error?.message || error || "Google sign-in failed.");

  if (code.includes("popup-blocked")) {
    return "Google sign-in popup was blocked. Allow popups for RodgersPC Command Center and try again.";
  }

  if (
    code.includes("popup-closed-by-user") ||
    code.includes("cancelled-popup-request")
  ) {
    return "Google sign-in was closed before completion. Click login again and finish the Google account chooser.";
  }

  if (code.includes("network-request-failed")) {
    return "Google sign-in failed because the network request failed. Check internet connection and try again.";
  }

  if (code.includes("unauthorized-domain")) {
    return "Google sign-in failed because this app domain is not authorized in Firebase Authentication settings.";
  }

  return message;
}

function logProjectStatus() {
  const projectId = (firebaseConfig as any)?.projectId;

  if (projectId !== EXPECTED_FIREBASE_PROJECT_ID) {
    console.error(
      `[Auth Firebase Project Mismatch] Expected ${EXPECTED_FIREBASE_PROJECT_ID}, but app is using ${projectId}.`,
    );
  } else {
    console.info(`[Auth Firebase Project OK] Using ${projectId}.`);
  }
}

function readFreshCachedDriveToken(): string | null {
  customerBenchModeActive = readCustomerBenchModeStorageFlag();

  if (customerBenchModeActive) {
    return cachedAccessToken && isTokenStillFresh(cachedAccessTokenExpiresAt)
      ? cachedAccessToken
      : null;
  }

  if (!cachedAccessToken || !isTokenStillFresh(cachedAccessTokenExpiresAt)) {
    cachedAccessToken = safeGetStoredToken();
  }

  return cachedAccessToken && isTokenStillFresh(cachedAccessTokenExpiresAt)
    ? cachedAccessToken
    : null;
}

function getCurrentAuthUser(): User | null {
  return auth.currentUser || lastAuthUser || null;
}

logProjectStatus();

customerBenchModeActive = readCustomerBenchModeStorageFlag();
if (!customerBenchModeActive) {
  cachedAccessToken = safeGetStoredToken();
}

export function getCachedDriveAccessToken(): string | null {
  return readFreshCachedDriveToken();
}

export function hasFreshDriveAccessToken(): boolean {
  return Boolean(readFreshCachedDriveToken());
}

export function getLastAuthUser(): User | null {
  return getCurrentAuthUser();
}

export function setCustomerBenchDeviceMode(isEnabled: boolean) {
  setCustomerBenchModeSessionFlag(isEnabled);

  if (isEnabled) {
    cachedAccessToken = null;
    cachedAccessTokenExpiresAt = 0;
    lastAuthUser = null;
    safeClearStoredToken();

    // If Joshua turns this on while already signed in, immediately break the persisted app session.
    // This keeps a customer/unknown browser from silently reopening the Command Center as Joshua.
    void (async () => {
      try {
        await signOut(auth);
      } catch {
        // already signed out
      }

      await applyAuthPersistence(true);
      clearAppOwnedWebStorage({ preserveBenchFlag: true, includeFirebase: true });
      await clearAppOwnedIndexedDbAndCaches();
      setCustomerBenchModeSessionFlag(true);
    })().catch((e) => {
      console.warn(
        "Unable to set Customer Bench memory-only Firebase persistence:",
        e,
      );
    });
    return;
  }

  void prepareNormalShopSignIn().catch((e) => {
    console.warn("Unable to restore normal shop auth persistence:", e);
  });
}

export function getCustomerBenchDeviceMode(): boolean {
  customerBenchModeActive = readCustomerBenchModeStorageFlag();
  return customerBenchModeActive;
}
export async function forceCustomerBenchDeviceReset() {
  customerBenchModeActive = true;
  setCustomerBenchModeSessionFlag(true);
  cachedAccessToken = null;
  cachedAccessTokenExpiresAt = 0;
  lastAuthUser = null;
  safeClearStoredToken();

  try {
    await signOut(auth);
  } catch {
    // already signed out
  }

  await applyAuthPersistence(true);
  clearAppOwnedWebStorage({ preserveBenchFlag: true, includeFirebase: true });
  await clearAppOwnedIndexedDbAndCaches();
  setCustomerBenchModeSessionFlag(true);
}


export async function clearCustomerBenchSessionStorage(options?: {
  preserveBenchFlag?: boolean;
}) {
  cachedAccessToken = null;
  cachedAccessTokenExpiresAt = 0;
  lastAuthUser = null;
  safeClearStoredToken();
  clearAppOwnedWebStorage({
    preserveBenchFlag: options?.preserveBenchFlag === true,
    includeFirebase: true,
  });
  await clearAppOwnedIndexedDbAndCaches();
}

/**
 * Reconnect Google Drive token without treating the whole app as broken.
 *
 * Use this before any Drive evidence load when:
 * - Drive Sync is ON
 * - Firebase user exists
 * - cached Drive OAuth token is missing/expired
 *
 * This supports:
 * - switching computers
 * - published reloads
 * - app refresh
 * - mode switching
 * - continuing saved chats
 * - Drive Sync OFF -> ON
 */
export async function reconnectGoogleDriveToken(
  options?: {
    reason?: string;
    forceAccountChooser?: boolean;
    isCustomerBenchMode?: boolean;
  },
): Promise<DriveTokenReconnectResult> {
  const existingToken = readFreshCachedDriveToken();
  const currentUser = getCurrentAuthUser();
  const activeBenchMode =
    options?.isCustomerBenchMode ?? readCustomerBenchModeStorageFlag();

  if (existingToken && currentUser) {
    return {
      user: currentUser,
      accessToken: existingToken,
      refreshed: false,
    };
  }

  if (reconnectInFlightPromise) {
    return reconnectInFlightPromise;
  }

  if (isSigningIn) {
    throw new Error(
      "Google sign-in is already in progress. Finish the Google popup, then retry.",
    );
  }

  reconnectInFlightPromise = (async () => {
    isReconnectingDriveToken = true;

    try {
      if (activeBenchMode) {
        await applyAuthPersistence(true);
        customerBenchModeActive = true;
        setCustomerBenchModeSessionFlag(true);
        safeClearStoredToken();
      } else {
        await applyAuthPersistence(false);
        customerBenchModeActive = false;
        setCustomerBenchModeSessionFlag(false);
      }

      const currentUserBeforePopup = getCurrentAuthUser();
      const shouldForcePrompt = activeBenchMode
        ? options?.forceAccountChooser !== false
        : options?.forceAccountChooser === true && !currentUserBeforePopup;

      const provider = buildGoogleProvider(activeBenchMode, {
        forcePrompt: shouldForcePrompt,
        loginHint: currentUserBeforePopup?.email || lastAuthUser?.email || null,
      });

      console.info("[Google Drive Token Reconnect Start]", {
        reason: options?.reason || "unspecified",
        customerBenchMode: activeBenchMode,
        hadFirebaseUser: Boolean(currentUserBeforePopup),
        hadCachedDriveToken: Boolean(existingToken),
        forcePrompt: shouldForcePrompt,
        firebaseProjectId: (firebaseConfig as any)?.projectId,
      });

      const result = await signInWithPopup(auth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential?.accessToken || null;

      lastAuthUser = result.user;

      if (!accessToken) {
        safeClearStoredToken();
        throw new Error(
          "Google sign-in completed, but no Google Drive access token was returned. Reconnect Drive and approve Drive access.",
        );
      }

      safeStoreToken(accessToken, activeBenchMode);

      console.info("[Google Drive Token Reconnect OK]", {
        uid: result.user.uid,
        email: result.user.email,
        hasDriveToken: true,
        reason: options?.reason || "unspecified",
        customerBenchMode: activeBenchMode,
        firebaseProjectId: (firebaseConfig as any)?.projectId,
      });

      return {
        user: result.user,
        accessToken,
        refreshed: true,
      };
    } catch (error: any) {
      const friendlyMessage = getFriendlyAuthError(error);

      console.error("[Google Drive Token Reconnect Failed]", {
        code: error?.code,
        message: error?.message,
        friendlyMessage,
        reason: options?.reason || "unspecified",
        customerBenchMode: activeBenchMode,
      });

      throw new Error(friendlyMessage);
    } finally {
      isReconnectingDriveToken = false;
      reconnectInFlightPromise = null;
    }
  })();

  return reconnectInFlightPromise;
}

/**
 * Central Drive token resolver.
 *
 * App.tsx should use this before:
 * - loading Drive hierarchy
 * - preloading Source of Truth
 * - fetching file content
 * - sending Drive-enabled chat requests
 * - switching modes while Drive Sync is ON
 */
export async function ensureGoogleDriveToken(
  options?: {
    reason?: string;
    allowPopup?: boolean;
    forceAccountChooser?: boolean;
    isCustomerBenchMode?: boolean;
  },
): Promise<DriveTokenReconnectResult | null> {
  const existingToken = readFreshCachedDriveToken();
  const currentUser = getCurrentAuthUser();

  if (existingToken && currentUser) {
    return {
      user: currentUser,
      accessToken: existingToken,
      refreshed: false,
    };
  }

  if (options?.allowPopup === false) {
    return null;
  }

  return reconnectGoogleDriveToken({
    reason: options?.reason || "drive_token_required",
    forceAccountChooser: options?.forceAccountChooser,
    isCustomerBenchMode: options?.isCustomerBenchMode,
  });
}

/**
 * Firebase Auth state listener.
 *
 * Normal RodgersPC device:
 * - Firebase auth persists locally.
 * - Drive OAuth token is reused only while fresh; expired tokens are discarded.
 *
 * Customer Bench Device Mode:
 * - Firebase auth uses memory-only persistence.
 * - Drive token stays memory-only.
 * - App-owned storage is cleared before sign-in, on sign-out, and on unload.
 */
export const initAuth = (
  onAuthSuccess: (user: User, token: string | null) => void,
  onAuthFailure: () => void,
) => {
  customerBenchModeActive = readCustomerBenchModeStorageFlag();

  void applyAuthPersistence(customerBenchModeActive).catch((e) => {
    console.warn("Unable to set initial Firebase auth persistence:", e);
  });

  return onAuthStateChanged(auth, async (user) => {
    customerBenchModeActive = readCustomerBenchModeStorageFlag();

    if (customerBenchModeActive) {
      // In customer-device mode, never pull tokens from local/session storage.
      if (!cachedAccessToken || !isTokenStillFresh(cachedAccessTokenExpiresAt)) {
        cachedAccessToken = null;
        cachedAccessTokenExpiresAt = 0;
      }
      try {
        localStorage.removeItem(DRIVE_TOKEN_KEY);
        localStorage.removeItem(DRIVE_TOKEN_EXPIRES_KEY);
        sessionStorage.removeItem(DRIVE_TOKEN_KEY);
        sessionStorage.removeItem(DRIVE_TOKEN_EXPIRES_KEY);
      } catch {
        // ignore
      }
    } else if (
      !cachedAccessToken ||
      !isTokenStillFresh(cachedAccessTokenExpiresAt)
    ) {
      cachedAccessToken = safeGetStoredToken();
    }

    if (user) {
      lastAuthUser = user;

      const activeToken = readFreshCachedDriveToken();

      console.info("[Auth State Restored]", {
        uid: user.uid,
        email: user.email,
        hasDriveToken: Boolean(activeToken),
        driveTokenFresh: Boolean(activeToken),
        customerBenchMode: customerBenchModeActive,
        firebaseProjectId: (firebaseConfig as any)?.projectId,
      });

      onAuthSuccess(user, activeToken);
      return;
    }

    lastAuthUser = null;
    cachedAccessToken = null;
    cachedAccessTokenExpiresAt = 0;

    console.info("[Auth Signed Out]");
    onAuthFailure();
  });
};

export const googleSignIn = async (
  isCustomerBenchMode = false,
): Promise<AuthResult | null> => {
  if (isSigningIn) {
    console.warn("[Google Sign-In] Sign-in already in progress.");
    return null;
  }

  isSigningIn = true;

  try {
    if (isCustomerBenchMode) {
      await prepareCustomerBenchSignIn();
    } else {
      await prepareNormalShopSignIn();
    }

    const provider = buildGoogleProvider(isCustomerBenchMode, {
      forcePrompt: isCustomerBenchMode,
    });

    console.info("[Google Sign-In Start]", {
      customerBenchMode: isCustomerBenchMode,
      firebaseProjectId: (firebaseConfig as any)?.projectId,
    });

    const result = await signInWithPopup(auth, provider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const accessToken = credential?.accessToken || null;

    lastAuthUser = result.user;

    if (accessToken) {
      safeStoreToken(accessToken, isCustomerBenchMode);
    } else {
      safeClearStoredToken();
      console.warn(
        "[Google Sign-In] Firebase sign-in succeeded but no Drive access token was returned. Reconnect Google with Drive consent if Drive tree is empty.",
      );
    }

    console.info("[Google Sign-In OK]", {
      uid: result.user.uid,
      email: result.user.email,
      hasDriveToken: Boolean(accessToken),
      driveTokenFresh: Boolean(
        accessToken && isTokenStillFresh(cachedAccessTokenExpiresAt),
      ),
      customerBenchMode: isCustomerBenchMode,
      firebaseProjectId: (firebaseConfig as any)?.projectId,
    });

    return {
      user: result.user,
      accessToken,
    };
  } catch (error: any) {
    const friendlyMessage = getFriendlyAuthError(error);
    console.error("[Google Sign-In Failed]", {
      code: error?.code,
      message: error?.message,
      friendlyMessage,
      customerBenchMode: isCustomerBenchMode,
    });

    throw new Error(friendlyMessage);
  } finally {
    isSigningIn = false;
  }
};

export const logout = async () => {
  const shouldClearCustomerBenchStorage =
    customerBenchModeActive || readCustomerBenchModeStorageFlag();

  try {
    await signOut(auth);
  } finally {
    cachedAccessToken = null;
    cachedAccessTokenExpiresAt = 0;
    lastAuthUser = null;
    safeClearStoredToken();

    if (shouldClearCustomerBenchStorage) {
      await clearCustomerBenchSessionStorage({ preserveBenchFlag: false });
      setCustomerBenchModeSessionFlag(false);
      await applyAuthPersistence(false).catch(() => undefined);
    }

    console.info("[Google Sign-Out Complete]", {
      customerBenchModeCleared: shouldClearCustomerBenchStorage,
    });
  }
};

function clearCustomerBenchRuntimeOnExit() {
  if (!customerBenchModeActive && !readCustomerBenchModeStorageFlag()) {
    return;
  }

  cachedAccessToken = null;
  cachedAccessTokenExpiresAt = 0;
  lastAuthUser = null;
  safeClearStoredToken();
  clearAppOwnedWebStorage({ preserveBenchFlag: false, includeFirebase: true });
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", clearCustomerBenchRuntimeOnExit);
  window.addEventListener("pagehide", clearCustomerBenchRuntimeOnExit);
}